import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';

export const getStyle = (flag: boolean, theme?: Theme) => {
  const headerBg =
    theme?.palette.mode === 'dark'
      ? theme.palette.action.hover
      : theme
        ? 'rgba(0, 0, 0, 0.13)'
        : 'rgba(0, 0, 0, 0.13)';
  return {
    overflowX: 'scroll',
    overflowY: 'hidden',
    '.MuiDataGrid-columnHeaderTitleContainerContent': {
      fontWeight: '600',
    },
    '.MuiDataGrid-columnHeaders': {
      backgroundColor: headerBg,
      borderRadius: '0',
      borderBottom: 'none', // Убираем разделитель под заголовками
    },
    '.MuiDataGrid-columnHeader': {
      minWidth: '150px',
    },
    '.MuiDataGrid-row': {
      cursor: flag ? 'pointer' : 'default',
      borderBottom: 'none', // Убираем разделители строк
      '&.super-app-theme--loading': {
        position: 'relative',
        pointerEvents: 'none',
        '& .loading-tooltip-wrapper': {
          position: 'absolute',
          top: '50%',
          left: 0,
          right: 0,
          height: '16px',
          transform: 'translateY(-50%)',
          zIndex: 4,
          margin: '0 16px',
          '& .MuiLinearProgress-root': {
            backgroundColor: 'rgba(33, 150, 243, 0.2)',
            borderRadius: '8px',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#1976d2',
              animation: 'progress-animation 2s ease-in-out infinite',
            },
          },
          '& .MuiTooltip-popper': {
            zIndex: 5,
          },
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          zIndex: 1,
        },
        '& .MuiDataGrid-cellContent': {
          position: 'relative',
          zIndex: 3,
        },
      },
      '@keyframes progress-animation': {
        '0%': { transform: 'translateX(-100%)' },
        '100%': { transform: 'translateX(100%)' },
      },
    },
    '.MuiDataGrid-cell': {
      borderBottom: 'none', // Убираем разделители ячеек
      outline: 'none !important', // Убираем синее выделение при клике
      '&:focus': {
        outline: 'none !important', // Убираем outline при фокусе
      },
      '&:focus-within': {
        outline: 'none !important', // Убираем outline для внутренних элементов
      },
    },
    '.MuiDataGrid-cellContent': {
      minHeight: 40,
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      zIndex: 3,
    },
    '.MuiDataGrid-columnHeaderTitle': {
      fontWeight: '600',
    },
    '.MuiSvgIcon-root': {
      fontSize: '25px',
    },
    '.MuiButtonBase-root': {
      padding: 0,
    },
    '.MuiDataGrid-footerContainer': {
      borderTop: 'none', // Убираем разделитель в футере
      color: theme?.palette.text.primary,
    },
    // Убираем выделение для всех состояний ячеек
    '.MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within, .MuiDataGrid-cell--withRenderer:focus':
      {
        outline: 'none !important',
        boxShadow: 'none !important',
      },
  };
};

const getRowBackground = (t: Theme) => t.palette.background.default;
const getColorIcon = (t: Theme) => t.palette.text.primary;
const getBackgroundColorIconHover = (t: Theme) =>
  t.palette.mode === 'dark' ? t.palette.action.hover : 'rgba(134, 134, 134, 0.712)';
const getBackgroundColorIconHoverSelected = (t: Theme) =>
  t.palette.mode === 'dark' ? t.palette.action.selected : 'rgba(0,0,0,0.5)';
const getHoverBackgroundColor = (t: Theme) =>
  t.palette.mode === 'dark' ? t.palette.action.hover : 'rgba(172, 172, 172, 0.507)';

const getSelectedBackgroundColor = (t: Theme) =>
  t.palette.mode === 'dark' ? '#4a5f6e' : '#667a8a';
const getSelectedHoverBackgroundColor = (t: Theme) =>
  t.palette.mode === 'dark' ? '#546e7a' : '#667a8a';

export const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: 'none', // Убираем общую границу таблицы
  '& .MuiDataGrid-row': {
    color: theme.palette.text.primary,
  },
  // Убираем выделение ячеек для всей таблицы
  '& .MuiDataGrid-cell': {
    outline: 'none !important',
    color: 'inherit',
    '&:focus': {
      outline: 'none !important',
    },
  },
  '& .super-app-theme': {
    '& .MuiButtonBase-root:hover': {
      backgroundColor: getBackgroundColorIconHover(theme),
      color: getColorIcon(theme),
    },
    '& .MuiSvgIcon-root': {
      color: getColorIcon(theme),
    },
    backgroundColor: getRowBackground(theme),
    '&:hover': {
      backgroundColor: getHoverBackgroundColor(theme),
    },
    '&.Mui-selected': {
      '& .MuiSvgIcon-root': {
        color: theme.palette.common.white,
      },
      '& .css-r6bn6-MuiChip-root': {
        color: '#fff',
      },
      '& span': {
        color: '#fff',
      },
      '& .css-1j047yb-MuiChip-root': {
        color: '#fff',
      },
      '& .MuiButtonBase-root:hover': {
        backgroundColor: getBackgroundColorIconHoverSelected(theme),
        color: getColorIcon(theme),
      },
      backgroundColor: getSelectedBackgroundColor(theme) + ' !important',
      color: '#fff',
      '&:hover': {
        backgroundColor: getSelectedHoverBackgroundColor(theme) + ' !important',
      },
    },
  },
  // Стили для выделенных групп email
  '& .selected-group': {
    backgroundColor: getSelectedBackgroundColor(theme) + ' !important',
    color: '#fff !important',
    '& .MuiDataGrid-cell': {
      color: '#fff !important',
      borderColor: getSelectedBackgroundColor(theme) + ' !important',
    },
    '&:hover': {
      backgroundColor: getSelectedHoverBackgroundColor(theme) + ' !important',
      '& .MuiDataGrid-cell': {
        color: '#fff !important',
        borderColor: getSelectedHoverBackgroundColor(theme) + ' !important',
      },
    },
  },
  // Стили для выделенных строк (при клике или клавиатурной навигации) - УСИЛЕННЫЕ СТИЛИ
  '& .MuiDataGrid-row.Mui-selected': {
    backgroundColor: getSelectedBackgroundColor(theme) + ' !important',
    '& .MuiDataGrid-cell': {
      color: '#fff !important',
      backgroundColor: getSelectedBackgroundColor(theme) + ' !important',
    },
    '&:hover': {
      backgroundColor: getSelectedHoverBackgroundColor(theme) + ' !important',
      '& .MuiDataGrid-cell': {
        backgroundColor: getSelectedHoverBackgroundColor(theme) + ' !important',
        color: '#fff !important',
      },
    },
  },
  // УСИЛЕННЫЕ СТИЛИ для всех выделенных ячеек
  '& .Mui-selected': {
    backgroundColor: getSelectedBackgroundColor(theme) + ' !important',
    '& .MuiDataGrid-cell': {
      backgroundColor: getSelectedBackgroundColor(theme) + ' !important',
      color: '#fff !important',
    },
  },
  // Специфичные стили для ячеек в выделенных строках
  '& .MuiDataGrid-cell.Mui-selected': {
    backgroundColor: getSelectedBackgroundColor(theme) + ' !important',
    color: '#fff !important',
  },
}));

export const StyledGridOverlay = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  '& .ant-empty-img-1': {
    fill: theme.palette.mode === 'light' ? '#aeb8c2' : '#262626',
  },
  '& .ant-empty-img-2': {
    fill: theme.palette.mode === 'light' ? '#f5f5f7' : '#595959',
  },
  '& .ant-empty-img-3': {
    fill: theme.palette.mode === 'light' ? '#dce0e6' : '#434343',
  },
  '& .ant-empty-img-4': {
    fill: theme.palette.mode === 'light' ? '#fff' : '#1c1c1c',
  },
  '& .ant-empty-img-5': {
    fillOpacity: theme.palette.mode === 'light' ? '0.8' : '0.08',
    fill: theme.palette.mode === 'light' ? '#f5f5f5' : '#fff',
  },
}));

export function CustomNoRowsOverlay() {
  return (
    <StyledGridOverlay>
      <svg
        style={{ flexShrink: 0 }}
        width="240"
        height="200"
        viewBox="0 0 184 152"
        aria-hidden
        focusable="false">
        <g fill="none" fillRule="evenodd">
          <g transform="translate(24 31.67)">
            <ellipse className="ant-empty-img-5" cx="67.797" cy="106.89" rx="67.797" ry="12.668" />
            <path
              className="ant-empty-img-1"
              d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
            />
            <path
              className="ant-empty-img-2"
              d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
            />
            <path
              className="ant-empty-img-3"
              d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
            />
          </g>
          <path
            className="ant-empty-img-3"
            d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
          />
          <g className="ant-empty-img-4" transform="translate(149.65 15.383)">
            <ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
            <path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
          </g>
        </g>
      </svg>
      <Box sx={{ mt: 1 }}>Данные отсутствуют</Box>
    </StyledGridOverlay>
  );
}
